// The console's spine, in the Nexus idiom the client sent: a brand-navy column
// under a white logo band with a bordered collapse control, closed-by-default
// section headings in plain text, an open section's pages hanging from a thin
// guide line, and the page you are on in white on a teal wash with a teal bar
// (colour but no icons, 2026-10-29).
// A workspace card with the signed-in person sits at the bottom, over the
// copyright line. Collapsed, it is a rail of icons.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { navFor, stagesFor, NAV_ITEMS } from "@/lib/nav";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { PanelLeftClose, PanelLeftOpen, X, ChevronsUpDown, ChevronDown } from "lucide-react";

const initialsOf = (name = "") => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "N";

export function Sidebar({ collapsed, setCollapsed, mobileOpen = false, onMobileClose }) {
  const location = useLocation();
  // The menu is the role's menu: items whose module the role does not own
  // are not drawn at all (rule 1 of the access spec), and a section with
  // nothing left in it vanishes with them.
  const { allowed, session } = useApp();
  const NAV = useMemo(() => navFor(allowed, session), [allowed, session]);
  // On a phone the drawer closes as soon as a page is chosen.
  useEffect(() => { onMobileClose?.(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [location.pathname]);
  // The menu is long: keep the current page in view after a reload, a deep
  // link or a jump from the header search. Only the menu itself scrolls.
  const navRef = useRef(null);
  useEffect(() => {
    const nav = navRef.current;
    const el = nav?.querySelector("[aria-current=page]");
    if (!nav || !el) return;
    const n = nav.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (r.top < n.top) nav.scrollTop -= n.top - r.top + 48;
    else if (r.bottom > n.bottom) nav.scrollTop += r.bottom - n.bottom + 48;
  }, [location.pathname, collapsed, NAV]);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onMobileClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, onMobileClose]);

  // An item may carry a query (App & Website Orders is Lab Orders with ?source=APP): it is
  // active only with that query, and the plain item is not while such a sibling matches.
  // An item may also own other paths (`also`: Radiology Centers owns a center's portal at
  // /app/centre, which has no menu item of its own for Novus staff).
  const isActive = (item) => {
    const [path, query] = item.path.split("?");
    const under = (p) => location.pathname === p || location.pathname.startsWith(p + "/");
    if ((item.also || []).some(under)) return true;
    if (!under(path)) return false;
    const search = decodeURIComponent(location.search);
    if (query) return search.includes(query);
    // A sibling one level down (/app/radiology/bookings under /app/radiology) owns its own pages.
    const deeper = (o) => { const p = o.path.split("?")[0]; return p !== path && p.startsWith(path + "/") && (location.pathname === p || location.pathname.startsWith(p + "/")); };
    if (NAV_ITEMS.some((o) => o !== item && deeper(o))) return false;
    return !NAV_ITEMS.some((o) => o !== item && o.path.startsWith(path + "?") && search.includes(o.path.split("?")[1]));
  };
  // Sections are closed by default; a heading's arrow opens and closes its section (the user,
  // 2026-10-26). The section holding the page you are on opens by itself so the page stays findable.
  const [openSections, setOpenSections] = useState(() => new Set());
  const toggleSection = (caption) => setOpenSections((cur) => { const n = new Set(cur); if (n.has(caption)) n.delete(caption); else n.add(caption); return n; });
  useEffect(() => {
    const here = NAV.find((g) => g.items.some((item) => isActive(item)));
    if (!here) return;
    const caption = here.section || "General";
    setOpenSections((cur) => (cur.has(caption) ? cur : new Set(cur).add(caption)));
    // a top-level item with sub-links (Today's Worklist) unfolds while its page is open
    const top = !here.section && here.items.find((item) => item.children && isActive(item));
    if (top) setOpenSections((cur) => (cur.has(top.path) ? cur : new Set(cur).add(top.path)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, NAV]);
  const focusRing = "outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--sidebar-active))] focus-visible:ring-offset-1 focus-visible:ring-offset-[hsl(var(--sidebar))]";

  // The rail (icons only) is a desktop idea; the drawer is always the full menu.
  const rail = collapsed;
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const toggle = (
    <button
      data-testid="sidebar-toggle"
      onClick={() => setCollapsed((c) => !c)}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      className={cn(
        "hidden h-7 w-7 shrink-0 items-center justify-center rounded-[7px] border border-slate-200 bg-white text-slate-500 lg:flex",
        "transition-colors duration-150 hover:bg-slate-100 hover:text-slate-900",
        focusRing
      )}
    >
      <ToggleIcon className="h-4 w-4" strokeWidth={1.75} />
    </button>
  );

  return (
    <>
      {/* Backdrop for the drawer */}
      <div
        aria-hidden="true"
        onClick={onMobileClose}
        className={cn("fixed inset-0 z-30 bg-black/40 backdrop-blur-[1px] transition-opacity duration-200 lg:hidden", mobileOpen ? "opacity-100" : "pointer-events-none opacity-0")}
      />
      <aside
        data-testid="sidebar"
        data-open={mobileOpen ? "true" : undefined}
        className={cn(
          "sidebar-shell fixed inset-y-0 left-0 z-40 flex h-screen w-full max-w-[85vw] flex-col border-r border-[hsl(var(--sidebar-border))] bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))] shadow-2xl transition-[transform,width] duration-200 sm:w-[280px]",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          "lg:sticky lg:top-0 lg:z-30 lg:max-w-none lg:translate-x-0 lg:shadow-none",
          rail ? "lg:w-[64px]" : "lg:w-[232px]"
        )}
      >
        {/* Brand + collapse: a white band (the wordmark needs a light ground, in dark mode too) */}
        <div className={cn("flex h-16 shrink-0 items-center gap-2 bg-white px-5", rail && "lg:flex-col lg:justify-center lg:gap-1.5 lg:px-0")} data-testid="sidebar-brand">
          <img src={`${process.env.PUBLIC_URL}/logo-mark.png`} alt="Novus Health Labs" className={cn("hidden h-8 w-8 rounded-[8px] bg-white object-contain p-0.5", rail && "lg:block")} />
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Novus Health Labs" className={cn("h-8 w-auto object-contain", rail && "lg:hidden")} />
          <span className={cn("ml-auto flex items-center", rail && "lg:ml-0")}>{toggle}</span>
          <button type="button" onClick={onMobileClose} aria-label="Close menu" data-testid="sidebar-close" className="ml-auto flex h-10 w-10 items-center justify-center rounded-[8px] text-slate-500 hover:bg-slate-100 lg:hidden"><X className="h-5 w-5" /></button>
        </div>
        <div aria-hidden="true" className="sidebar-accent-line h-[2px] shrink-0" />
        {/* Menu: plain text on navy, teal the one accent (the user, 2026-10-29: colour, no icons,
            professional). Item icons are drawn only in the collapsed rail, where a label does not fit. */}
        <nav ref={navRef} className={cn("sidebar-nav flex-1 overflow-y-auto px-3 pb-4 pt-3", rail && "lg:px-3")} aria-label="Main navigation" data-testid="sidebar-nav" data-items={NAV.reduce((n, g) => n + g.items.length, 0)}>
          {NAV.map((group, gi) => {
            const caption = group.section || "General";
            // The group without a section (Dashboard, Today's Worklist) has no heading: its items stand at
            // the top level, always shown (the user, 2026-10-30: "no need General").
            const headless = !group.section;
            const isOpen = rail || headless || openSections.has(caption);
            return (
              <div key={gi} data-open={!rail && isOpen && !headless ? "true" : undefined}
                className={cn(rail ? gi > 0 && "mt-3 border-t border-[hsl(var(--sidebar-border))] pt-3" : headless ? "mb-1" : cn("nav-group", gi > 0 && "mt-0.5", isOpen && "my-1 pb-2"))}>
                {rail || headless ? (
                  rail ? <span className="sr-only">{caption}</span> : null
                ) : (
                  <button type="button" data-testid={`nav-section-${gi}`} aria-expanded={isOpen} onClick={() => toggleSection(caption)} title={isOpen ? `Close ${caption}` : `Open ${caption}`}
                    className={cn("nav-head flex h-9 w-full items-center gap-2 rounded-[8px] px-3 text-left text-[13px] font-semibold transition-colors duration-150", focusRing)}>
                    <span className="min-w-0 flex-1 truncate">{caption}</span>
                    <ChevronDown aria-hidden="true" className="nav-head-chev h-4 w-4 shrink-0" strokeWidth={2} />
                  </button>
                )}
                {isOpen && <div className={cn(rail ? "space-y-0.5" : headless ? "space-y-0.5" : "nav-tree nav-open ml-3 mr-1.5 mt-0.5 space-y-px")} data-testid={rail ? undefined : `nav-section-items-${gi}`}>
                  {group.items.map((item) => {
                    const active = isActive(item);
                    const Icon = item.icon;
                    // Sub-links (Specimens → Pending Samples, …; Lab Orders → Walk In / B2B) always show under
                    // their item (2026-10-27). Each is the page with one query value (?stage= / ?channel=); Specimens
                    // opens on its first stage when none is given, the order lists show everything.
                    const kids = item.children ? stagesFor(session?.role, item.children).filter((c) => !c.hideFor?.(session)) : [];
                    const current = new URLSearchParams(location.search).get(kids[0]?.param || "stage") || (item.firstChildByDefault ? kids[0]?.stage : null);
                    // Top level (no section): an item with sub-links is a dropdown (the user, 2026-10-30). The
                    // name opens its page and unfolds it; the arrow alone folds / unfolds.
                    const dropdown = headless && !rail && kids.length > 0;
                    const unfolded = !dropdown || openSections.has(item.path);
                    return (
                      <div key={item.path} className={cn(dropdown && "relative")}>
                      {/* A plain Link: NavLink would mark a parent path current too (/app/centre under
                          /app/centre/orders); isActive() alone decides which row is the page. */}
                      <Link
                        to={item.path}
                        data-testid={item.testid}
                        title={item.label}
                        onClick={dropdown ? () => setOpenSections((cur) => new Set(cur).add(item.path)) : undefined}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "group relative flex items-center text-[13px] transition-colors duration-150",
                          focusRing,
                          rail ? "h-10 justify-center rounded-[10px] px-0" : headless ? "h-9 gap-2 rounded-[8px] px-3 !font-semibold" : "nav-row h-8 gap-2 rounded-r-[8px] pl-3 pr-2",
                          active
                            ? "font-semibold text-[hsl(var(--sidebar-active-text))]"
                            : "font-medium text-[hsl(var(--sidebar-foreground))] hover:bg-[hsl(var(--sidebar-raised)/0.7)] hover:text-[hsl(var(--sidebar-heading))]"
                        )}
                      >
                        {rail && (
                          <span aria-hidden="true" className="nav-tile flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px]">
                            <Icon className="h-[17px] w-[17px]" strokeWidth={1.9} />
                          </span>
                        )}
                        {!rail && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
                        {!rail && item.badge != null && (
                          <span className={cn(
                            "ml-auto shrink-0 rounded-[6px] px-1.5 py-px text-[11px] font-semibold leading-4 tabular-nums",
                            item.badgeTone === "accent"
                              ? "bg-[hsl(var(--sidebar-active)/0.12)] text-[hsl(var(--sidebar-active))]"
                              : "bg-[hsl(var(--sidebar-raised))] text-[hsl(var(--sidebar-foreground))]"
                          )}>{item.badge}</span>
                        )}
                        {rail && item.badge != null && <span aria-hidden="true" className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--sidebar-active))]" />}
                        {dropdown && <span aria-hidden="true" className="w-5 shrink-0" />}
                      </Link>
                      {dropdown && (
                        <button type="button" data-testid={`${item.testid}-toggle`} aria-expanded={unfolded} aria-label={unfolded ? `Close ${item.label}` : `Open ${item.label}`} title={unfolded ? `Close ${item.label}` : `Open ${item.label}`}
                          onClick={() => toggleSection(item.path)}
                          className={cn("nav-head absolute right-1 top-[18px] flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-[6px]", focusRing)}>
                          <ChevronDown aria-hidden="true" className="nav-head-chev h-4 w-4" strokeWidth={2} />
                        </button>
                      )}
                      {!rail && kids.length > 0 && unfolded && (
                        <div className="nav-tree mb-1 ml-6 mt-px space-y-px" data-testid={`${item.testid}-children`}>
                          {kids.map((c) => {
                            // the list page, or its new-order form (/app/workspace/new?channel=b2b): the sub-link the
                            // order is being billed under stays marked (the user, 2026-10-30)
                            const on = (location.pathname === item.path || location.pathname === `${item.path}/new`) && current === c.stage;
                            return (
                              <Link key={c.path} to={c.path} data-testid={c.testid} aria-current={on ? "true" : undefined}
                                className={cn("nav-row flex h-7 items-center rounded-r-[8px] pl-3 pr-2 text-[12.5px] transition-colors duration-150", focusRing,
                                  on ? "font-semibold text-[hsl(var(--sidebar-active-text))]" : "font-medium text-[hsl(var(--sidebar-muted))] hover:bg-[hsl(var(--sidebar-raised)/0.7)] hover:text-[hsl(var(--sidebar-heading))]")}>
                                {c.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                      </div>
                    );
                  })}
                </div>}
              </div>
            );
          })}
        </nav>

        {/* Workspace card: the signed-in person and their role; a step to "My access & password". */}
        {session && (
          <div className={cn("shrink-0 border-t border-[hsl(var(--sidebar-border))] px-2.5 pb-3 pt-4", rail && "lg:px-3")}>
            <NavLink
              to="/app/help"
              data-testid="sidebar-user"
              title={rail ? `${session.name} · ${session.roleLabel || ""}` : "My access & password"}
              className={cn(
                "group flex items-center gap-3 rounded-[12px] border border-[hsl(var(--sidebar-border))] bg-[hsl(var(--sidebar-raised)/0.6)] px-3 py-2.5 transition-colors duration-150 hover:bg-[hsl(var(--sidebar-raised))]",
                rail && "lg:justify-center lg:border-0 lg:bg-transparent lg:px-0 lg:py-1",
                focusRing
              )}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] sidebar-avatar text-[12px] font-bold text-white">{initialsOf(session.name)}</span>
              {!rail && (
                <>
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block truncate text-[11px] text-[hsl(var(--sidebar-muted))]">{session.roleLabel || session.role}</span>
                    <span className="block truncate text-[13px] font-semibold text-[hsl(var(--sidebar-heading))]">{session.name}</span>
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 text-[hsl(var(--sidebar-muted))]" strokeWidth={1.75} aria-hidden="true" />
                </>
              )}
            </NavLink>
            {!rail && <p className="mt-3 text-center text-[11px] text-[hsl(var(--sidebar-muted))]">© {new Date().getFullYear()} Novus Health Labs</p>}
          </div>
        )}
      </aside>
    </>
  );
}
